import { createRouter, createWebHistory } from 'vue-router';
import _ from 'lodash';
import type { NavigationGuardNext, RouteLocationNormalized, RouteLocationResolved, Router } from 'vue-router';
import { setCurrentMfeRoute, redirectToNotFound } from 'host/mfeBridge';
import { createRoutes } from '@Router/routes';
import { getBasePath, getHostRouter } from '@Src/mfe.config';
import { hostRouteSyncState } from '@Router/hostRouteSyncState';
import { useRequestFlowStore } from '@Stores/requestFlow.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';

type SyncRequestSessionOutcome =
    | { kind: 'abort' }
    | { kind: 'redirect'; name: string }
    | { kind: 'continue' };

const basePath = getBasePath() ?? '/';
const router = createRouter({
    history: createWebHistory(),
    routes: createRoutes(basePath),
});
console.log(
    '[Request MFE Router] Routes created:',
    router.getRoutes().map((r) => ({ name: r.name, path: r.path })),
);

// ensure client flows are loaded
const prepareRequestFlow = async (): Promise<void> => {
    const requestFlowStore = useRequestFlowStore();
    const isFlowEmpty = _.isEmpty(requestFlowStore.flowSteps);

    if (isFlowEmpty) {
        await requestFlowStore.loadRequestFlow();
    }
};

// Sync mfe with session data. If another tab overwrote the activity session, abort and send user to host not-found.
const syncRequestSession = async (loadRequestFlowOverride: boolean,forceFreshSync: boolean): Promise<SyncRequestSessionOutcome> => {
    const requestSessionStore = useRequestSessionStore();
    const requestFlowStore = useRequestFlowStore();

    const { sessionContextOverwritten, requestSummaryChanged, requestSummary } =
        await requestSessionStore.syncSession({ force: forceFreshSync || loadRequestFlowOverride });

    if (sessionContextOverwritten) {
        redirectToNotFound();
        return { kind: 'abort' };
    }

    if (requestSummary?.status === 'Canceled') {
        return { kind: 'redirect', name: 'request-canceled' };
    }

    if (requestSummary?.isExpired) {
        return { kind: 'redirect', name: 'request-expired' };
    }

    if (requestSummaryChanged || loadRequestFlowOverride) {
        await requestFlowStore.loadRequestFlow();
    } else {
        await prepareRequestFlow();
    }

    return { kind: 'continue' };
};

const setCurrentFlowRoute = (to: RouteLocationNormalized): void => {
    const requestFlowStore = useRequestFlowStore();
    requestFlowStore.setCurrentRoute(to.name as string);
};

const resolveHostLocation = (mfeRouter: Router): RouteLocationResolved | null => {
    const hostPath = getHostRouter()?.currentRoute?.value?.fullPath;
    return hostPath ? mfeRouter.resolve(hostPath) : null;
};

const redirectIfHostSyncMismatch = (
    to: RouteLocationNormalized,
    hostLocation: RouteLocationResolved | null,
    next: NavigationGuardNext,
): boolean => {
    if (hostRouteSyncState.isApplyingHostRoute && hostLocation) {
        if (to.fullPath !== hostLocation.fullPath) {
            next(hostLocation.fullPath);
            return true;
        }
    }

    return false;
};

const enforceFlowRoute = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<boolean> => {
    const loadRequestFlowOverride = Boolean(to.meta?.loadRequestFlowOverride ?? false);
    const isBootstrap = Boolean(to.meta?.bootstrap);

    // DS-2779 A.1 — force a fresh BFF sync on bootstrap entry and on the
    // wire-instructions-ready override; intra-flow transitions reuse the
    // cached request-summary in the store (per SYNC_SESSION_TTL_MS) to
    // eliminate the ~7s "Continue" hangs in the send flow.
    const outcome = await syncRequestSession(loadRequestFlowOverride, isBootstrap);
    if (outcome.kind === 'abort') {
        next(false);
        return true;
    }
    if (outcome.kind === 'redirect') {
        // PAY-3357 — flow now owns routing; release the host-sync flag so this redirect isn't bounced.
        hostRouteSyncState.isApplyingHostRoute = false;
        next({ name: outcome.name });
        return true;
    }

    const requestFlowStore = useRequestFlowStore();
    if (!requestFlowStore.hasRouteInFlow(to.name as string)) {
        const fromNotInFlow = !requestFlowStore.hasRouteInFlow(from.name as string);
        const redirectTarget = fromNotInFlow ? _.head(requestFlowStore.flowSteps) : null;

        if (redirectTarget) {
            // PAY-3357 — honor the welcome→startup flow-entry redirect instead of bouncing it back.
            hostRouteSyncState.isApplyingHostRoute = false;
            next({ name: redirectTarget.routeName, params: to.params, query: to.query });
        } else {
            next(false);
        }
        return true;
    }

    setCurrentFlowRoute(to);
    return false;
};

router.beforeEach(async (to, from, next) => {
    const hostLocation = resolveHostLocation(router);

    if (redirectIfHostSyncMismatch(to, hostLocation, next)) {
        return;
    }

    setCurrentMfeRoute(to);

    if (!to.meta?.requiresFlow) {
        next();
        return;
    }

    if (await enforceFlowRoute(to, from, next)) {
        return;
    }

    next();
});

export default router;
